import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme/colors';
import { StyledTextField } from '../../components/products/ProductFilters/ProductFilters.styles';
import { DecimalNumberInput } from '../../components/ui';
import CatalogAutocomplete from '../../components/common/CatalogAutocomplete';
import type { Location } from '../../api/models/Location';
import type { Category } from '../../api/models/Category';
import { SafetyStockService } from '../../api/services/SafetyStockService';
import type { BulkUpdateSafetyStockDto } from '../../api/models/BulkUpdateSafetyStockDto';
import { dialogZoomPaperSx } from '../../components/ui/DialogZoomTransition';

export type BulkScope = 'all' | 'location' | 'productType';

export interface BulkUpdateSafetyStockModalProps {
  open: boolean;
  onClose: () => void;
  locations: Location[];
  categories: Category[];
  onSuccess: (updatedCount?: number) => void;
}

const SCOPE_LABELS: Record<BulkScope, string> = {
  all: 'Todos',
  location: 'Por ubicación',
  productType: 'Por tipo de producto',
};

const BulkUpdateSafetyStockModal: React.FC<BulkUpdateSafetyStockModalProps> = ({
  open,
  onClose,
  locations,
  categories,
  onSuccess,
}) => {
  const [safetyStock, setSafetyStock] = useState<string>('');
  const [scope, setScope] = useState<BulkScope>('all');
  const [locationId, setLocationId] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);

  const handleClose = () => {
    setSafetyStock('');
    setScope('all');
    setLocationId('');
    setCategoryId('');
    setError(null);
    setConfirmed(false);
    onClose();
  };

  const handleScopeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScope(e.target.value as BulkScope);
    setLocationId('');
    setCategoryId('');
  };

  const handleSave = async () => {
    const value = parseInt(safetyStock, 10);
    if (isNaN(value) || value < 0) {
      setError('El stock de seguridad debe ser un número entero mayor o igual a 0.');
      return;
    }
    if (scope === 'location' && !locationId) {
      setError('Seleccione una ubicación.');
      return;
    }
    if (scope === 'productType' && !categoryId) {
      setError('Seleccione un tipo de producto.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const body: {
        safetyStock: number;
        scope: BulkScope;
        locationId?: number;
        categoryId?: number;
      } = {
        safetyStock: value,
        scope,
      };
      if (scope === 'location' && locationId) {
        body.locationId = parseInt(locationId, 10);
      }
      if (scope === 'productType' && categoryId) {
        body.categoryId = parseInt(categoryId, 10);
      }
      const res = await SafetyStockService.productLocationSafetyStockControllerBulkUpdate(
        body as BulkUpdateSafetyStockDto,
      );
      onSuccess(res?.updated ?? 0);
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar el stock de seguridad.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: dialogZoomPaperSx({
          borderRadius: 2,
          // Sin overflow:hidden: recorta labels outlined y botones del pie cuando el ancho es justo.
          overflow: 'visible',
          boxShadow: '0 20px 60px rgba(2, 6, 23, 0.18)',
          margin: 'auto',
          width: '100%',
          maxWidth: { xs: 'min(600px, calc(100% - 32px))', sm: '600px' },
          bgcolor: 'background.paper',
        }),
      }}
    >
      <DialogTitle
        sx={(theme) => ({
          padding: '14px 20px',
          borderBottom: `1px solid ${theme.palette.divider}`,
          background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 600,
          fontSize: '1rem',
        })}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            minWidth: 0,
            justifyContent: 'flex-start',
          }}
        >
          <EditIcon sx={{ fontSize: 22, flexShrink: 0 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#ffffff',
              fontSize: '1rem',
              overflow: 'visible',
              textOverflow: 'clip',
            }}
          >
            Edición personalizada
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            color: '#ffffff',
            '&:hover': { color: alpha('#fff', 0.85) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={(theme) => ({
          padding: '48px 28px 32px',
          overflowY: 'auto',
          overflowX: 'visible',
          minHeight: 120,
          backgroundColor: theme.palette.background.paper,
        })}
      >
        {error && (
          <Alert
            severity="error"
            sx={(theme) => ({
              mb: 2,
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor:
                theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.15) : '#fee2e2',
              color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#dc2626',
              border: `1px solid ${theme.palette.mode === 'dark' ? alpha(theme.palette.error.main, 0.35) : '#fecaca'}`,
              '& .MuiAlert-icon': {
                color: theme.palette.mode === 'dark' ? theme.palette.error.light : '#dc2626',
              },
            })}
          >
            {error}
          </Alert>
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            mt: 3,
            width: '100%',
            minWidth: 0,
            '& .MuiTextField-root': {
              maxWidth: '100%',
            },
            '& .MuiInputLabel-root': {
              whiteSpace: 'normal',
              lineHeight: 1.3,
            },
            '& .MuiInputLabel-root.MuiInputLabel-shrink': {
              textOverflow: 'clip',
              overflow: 'visible',
            },
          }}
        >
          <StyledTextField
            select
            fullWidth
            variant="outlined"
            size="medium"
            label="Ámbito"
            value={scope}
            onChange={handleScopeChange}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="all">{SCOPE_LABELS.all}</MenuItem>
            <MenuItem value="location">{SCOPE_LABELS.location}</MenuItem>
            <MenuItem value="productType">{SCOPE_LABELS.productType}</MenuItem>
          </StyledTextField>

          {scope === 'location' && (
            <StyledTextField
              select
              fullWidth
              variant="outlined"
              size="medium"
              label="Ubicación *"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">
                <em>Seleccione ubicación</em>
              </MenuItem>
              {locations.map((loc) => (
                <MenuItem key={loc.id} value={String(loc.id)}>
                  {loc.name}
                </MenuItem>
              ))}
            </StyledTextField>
          )}

          {scope === 'productType' && (
            <CatalogAutocomplete
              catalogType="category"
              value={categoryId}
              onChange={(value) => setCategoryId(value)}
              pinOptions={categories}
              label="Tipo de producto *"
              required
              disabled={loading}
              size="medium"
              textFieldProps={{ variant: 'outlined', fullWidth: true }}
            />
          )}

          <DecimalNumberInput
            fullWidth
            variant="outlined"
            size="medium"
            label="Nuevo valor de stock de seguridad *"
            value={safetyStock}
            onChange={(val) => setSafetyStock(val != null ? String(Math.trunc(val)) : '')}
            placeholder="0"
            decimalPlaces={0}
            min={0}
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
                disabled={loading}
                sx={{
                  color: colors.success.main,
                  '&.Mui-checked': { color: colors.success.main },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                Confirmar actualización
              </Typography>
            }
            sx={{ marginLeft: 0 }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={(theme) => ({
          padding: '16px 24px',
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            theme.palette.mode === 'dark' ? theme.palette.background.paper : theme.palette.grey[50],
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
          gap: 1.5,
          rowGap: 1.5,
          overflow: 'visible',
        })}
      >
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={(theme) => ({
            minWidth: '120px',
            padding: '9px 18px',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            color: theme.palette.text.secondary,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.06)
                : theme.palette.grey[50],
            border: `1px solid ${theme.palette.divider}`,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.text.primary,
            },
          })}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || safetyStock === '' || !confirmed}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={(theme) => ({
            minWidth: '120px',
            padding: '9px 18px',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '14px',
            background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
            color: '#ffffff',
            boxShadow: 'none',
            '&:hover': {
              background: 'linear-gradient(to right, #0891b2, #059669)',
              boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
            },
            '&:disabled': {
              background: colors.grey[400],
              color: '#ffffff',
              opacity: theme.palette.mode === 'dark' ? 0.5 : 0.6,
              cursor: 'not-allowed',
              boxShadow: 'none',
            },
          })}
        >
          {loading ? 'Actualizando...' : 'Actualizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkUpdateSafetyStockModal;
